import { useEffect, useState } from "react";
import { Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { api } from "./lib/api";
import { Me, MeContext, canUseTool } from "./lib/me";
import Layout from "./shell/Layout";
import Login from "./shell/Login";
import Setup from "./shell/Setup";
import Invite from "./shell/Invite";
import Home from "./shell/Home";
import ComingSoon from "./shell/ComingSoon";
import Admin from "./shell/Admin";

import CollectionDashboard from "./tools/collection/pages/Dashboard";
import CollectionClients   from "./tools/collection/pages/Clients";
import CollectionImport    from "./tools/collection/pages/Import";
import CollectionReview    from "./tools/collection/pages/Review";
import CollectionTemplates from "./tools/collection/pages/Templates";
import CollectionSend      from "./tools/collection/pages/Send";
import CollectionSendLog   from "./tools/collection/pages/SendLog";

import PayrollDashboard    from "./tools/payroll/pages/DashboardPage";
import PayrollEmployees    from "./tools/payroll/pages/EmployeesPage";
import PayrollPeriods      from "./tools/payroll/pages/PayrollPage";
import PayrollSlips        from "./tools/payroll/pages/SalarySlipsPage";
import PayrollReports      from "./tools/payroll/pages/ReportsPage";
import PayrollSettings     from "./tools/payroll/pages/SettingsPage";
import PayrollImport       from "./tools/payroll/pages/ImportPage";

import { Dashboard    as RawWhtDashboard }    from "./tools/wht/pages/Dashboard";
import { Import       as RawWhtImport }       from "./tools/wht/pages/Import";
import { ClientList   as RawWhtClientList }   from "./tools/wht/pages/ClientList";
import { ClientDetail as RawWhtClientDetail } from "./tools/wht/pages/ClientDetail";
import { EmailCompose as RawWhtEmail }        from "./tools/wht/pages/EmailCompose";
import { Templates    as RawWhtTemplates }    from "./tools/wht/pages/Templates";
import { Reports      as RawWhtReports }      from "./tools/wht/pages/Reports";
import { PageBoundary } from "./tools/wht/components/PageBoundary";

import RawMondayApp from "./tools/monday/MondayApp";
const MondayApp = () => <PageBoundary pageName="Contacts"><RawMondayApp /></PageBoundary>;

import OutreachDashboard    from "./tools/outreach/pages/Dashboard";
import OutreachContacts     from "./tools/outreach/pages/Contacts";
import OutreachReview       from "./tools/outreach/pages/QuickReview";
import OutreachImport       from "./tools/outreach/pages/Import";
import OutreachCampaigns    from "./tools/outreach/pages/Campaigns";
import OutreachCampaignEdit from "./tools/outreach/pages/CampaignEdit";
import OutreachLogs         from "./tools/outreach/pages/Logs";
import OutreachVerifyLog    from "./tools/outreach/pages/VerifyLog";
import OutreachResearch     from "./tools/outreach/pages/Research";
import OutreachResearchRun  from "./tools/outreach/pages/ResearchRun";
import OutreachSenders      from "./tools/outreach/pages/Senders";
import OutreachRoles        from "./tools/outreach/pages/Roles";
import OutreachScope        from "./tools/outreach/components/Scope";
import { isMasterScope }    from "./tools/outreach/lib/business";

const WhtDashboard    = () => <PageBoundary pageName="Dashboard"><RawWhtDashboard /></PageBoundary>;
const WhtImport       = () => <PageBoundary pageName="Import"><RawWhtImport /></PageBoundary>;
const WhtClientList   = () => <PageBoundary pageName="Clients"><RawWhtClientList /></PageBoundary>;
const WhtClientDetail = () => <PageBoundary pageName="Client detail"><RawWhtClientDetail /></PageBoundary>;
const WhtEmail        = () => <PageBoundary pageName="Email"><RawWhtEmail /></PageBoundary>;
const WhtTemplates    = () => <PageBoundary pageName="Templates"><RawWhtTemplates /></PageBoundary>;
const WhtReports      = () => <PageBoundary pageName="Reports"><RawWhtReports /></PageBoundary>;

type AuthState = "loading" | "needs_setup" | "needs_login" | "authed";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

export default function App() {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const nav = useNavigate();
  const loc = useLocation();

  async function refreshAuth() {
    try {
      // Short timeout: this call decides what the whole app renders, so if it stalls we fall
      // through to the login screen instead of leaving "Loading…" on screen indefinitely.
      const s = await api<{ setupComplete: boolean; authenticated: boolean; user: Me | null }>(
        "/api/auth/status", { timeoutMs: 15_000 },
      );
      setMe(s.user || null);
      if (!s.setupComplete) setAuth("needs_setup");
      else if (!s.authenticated) setAuth("needs_login");
      else setAuth("authed");
    } catch {
      setMe(null);
      setAuth("needs_login");
    }
  }
  useEffect(() => { refreshAuth(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const isPublicRoute = loc.pathname === "/login" || loc.pathname === "/setup" || loc.pathname === "/invite";

  useEffect(() => {
    if (auth === "needs_setup" && loc.pathname !== "/setup") nav("/setup", { replace: true });
    if (auth === "needs_login" && !isPublicRoute) nav("/login", { replace: true });
    if (auth === "authed" && (loc.pathname === "/login" || loc.pathname === "/setup" || loc.pathname === "/invite")) {
      nav("/", { replace: true });
    }
  }, [auth, loc.pathname, nav, isPublicRoute]);

  if (auth === "loading") {
    return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <MeContext.Provider value={me}>
      <QueryClientProvider client={queryClient}>
        <Routes>
          <Route path="/setup"  element={<Setup onDone={refreshAuth} />} />
          <Route path="/login"  element={<Login onDone={refreshAuth} />} />
          <Route path="/invite" element={<Invite onDone={refreshAuth} />} />
          <Route element={<Layout onLogout={refreshAuth} />}>
            <Route path="/" element={<Home />} />

            <Route path="/admin" element={me?.role === "admin" ? <Admin /> : <Navigate to="/" replace />} />

            {/* Payment Collection */}
            <Route path="/collection" element={canUseTool(me, "collection") ? <Navigate to="/collection/dashboard" replace /> : <Navigate to="/" replace />} />
            <Route path="/collection/dashboard" element={canUseTool(me, "collection") ? <CollectionDashboard /> : <Navigate to="/" replace />} />
            <Route path="/collection/clients"   element={canUseTool(me, "collection") ? <CollectionClients />   : <Navigate to="/" replace />} />
            <Route path="/collection/import"    element={canUseTool(me, "collection") ? <CollectionImport />    : <Navigate to="/" replace />} />
            <Route path="/collection/review"    element={canUseTool(me, "collection") ? <CollectionReview />    : <Navigate to="/" replace />} />
            <Route path="/collection/templates" element={canUseTool(me, "collection") ? <CollectionTemplates /> : <Navigate to="/" replace />} />
            <Route path="/collection/send"      element={canUseTool(me, "collection") ? <CollectionSend />      : <Navigate to="/" replace />} />
            <Route path="/collection/log"       element={canUseTool(me, "collection") ? <CollectionSendLog />   : <Navigate to="/" replace />} />

            {/* Payroll */}
            <Route path="/payroll"               element={canUseTool(me, "payroll") ? <PayrollDashboard /> : <Navigate to="/" replace />} />
            <Route path="/payroll/employees"     element={canUseTool(me, "payroll") ? <PayrollEmployees /> : <Navigate to="/" replace />} />
            <Route path="/payroll/periods"       element={canUseTool(me, "payroll") ? <PayrollPeriods />   : <Navigate to="/" replace />} />
            <Route path="/payroll/slips"         element={canUseTool(me, "payroll") ? <PayrollSlips />     : <Navigate to="/" replace />} />
            <Route path="/payroll/reports"       element={canUseTool(me, "payroll") ? <PayrollReports />   : <Navigate to="/" replace />} />
            <Route path="/payroll/settings"      element={canUseTool(me, "payroll") ? <PayrollSettings />  : <Navigate to="/" replace />} />
            <Route path="/payroll/import"        element={canUseTool(me, "payroll") ? <PayrollImport />    : <Navigate to="/" replace />} />

            {/* WHT Monitor */}
            <Route path="/wht"                element={canUseTool(me, "wht") ? <WhtDashboard />    : <Navigate to="/" replace />} />
            <Route path="/wht/import"         element={canUseTool(me, "wht") ? <WhtImport />       : <Navigate to="/" replace />} />
            <Route path="/wht/clients"        element={canUseTool(me, "wht") ? <WhtClientList />   : <Navigate to="/" replace />} />
            <Route path="/wht/clients/:id"    element={canUseTool(me, "wht") ? <WhtClientDetail /> : <Navigate to="/" replace />} />
            <Route path="/wht/email"          element={canUseTool(me, "wht") ? <WhtEmail />        : <Navigate to="/" replace />} />
            <Route path="/wht/templates"      element={canUseTool(me, "wht") ? <WhtTemplates />    : <Navigate to="/" replace />} />
            <Route path="/wht/reports"        element={canUseTool(me, "wht") ? <WhtReports />      : <Navigate to="/" replace />} />

            {/* Contacts (Monday) — single full-app route; the inner state handles board selection */}
            <Route path="/monday/*" element={canUseTool(me, "monday") ? <MondayApp /> : <Navigate to="/" replace />} />

            {/* Outreach — each wrapped so a render error shows a recoverable card, not a blank app */}
            <Route path="/outreach"               element={canUseTool(me, "outreach") ? <PageBoundary pageName="Outreach dashboard">{isMasterScope() ? <Navigate to="/outreach/research" replace /> : <OutreachDashboard />}</PageBoundary>    : <Navigate to="/" replace />} />
            <Route path="/outreach/contacts"      element={canUseTool(me, "outreach") ? <PageBoundary pageName="Contacts"><OutreachScope need="company"><OutreachContacts /></OutreachScope></PageBoundary>               : <Navigate to="/" replace />} />
            <Route path="/outreach/review"        element={canUseTool(me, "outreach") ? <PageBoundary pageName="Quick review"><OutreachScope need="company"><OutreachReview /></OutreachScope></PageBoundary>            : <Navigate to="/" replace />} />
            <Route path="/outreach/import"        element={canUseTool(me, "outreach") ? <PageBoundary pageName="Import"><OutreachScope need="master"><OutreachImport /></OutreachScope></PageBoundary>                  : <Navigate to="/" replace />} />
            <Route path="/outreach/campaigns"     element={canUseTool(me, "outreach") ? <PageBoundary pageName="Campaigns"><OutreachScope need="master"><OutreachCampaigns /></OutreachScope></PageBoundary>            : <Navigate to="/" replace />} />
            <Route path="/outreach/campaigns/:id" element={canUseTool(me, "outreach") ? <PageBoundary pageName="Campaign"><OutreachScope need="master"><OutreachCampaignEdit /></OutreachScope></PageBoundary>          : <Navigate to="/" replace />} />
            <Route path="/outreach/logs"          element={canUseTool(me, "outreach") ? <PageBoundary pageName="Email log"><OutreachScope need="company"><OutreachLogs /></OutreachScope></PageBoundary>                : <Navigate to="/" replace />} />
            <Route path="/outreach/verify-log"    element={canUseTool(me, "outreach") ? <PageBoundary pageName="Verification log"><OutreachScope need="company"><OutreachVerifyLog /></OutreachScope></PageBoundary>   : <Navigate to="/" replace />} />
            <Route path="/outreach/research"      element={canUseTool(me, "outreach") ? <PageBoundary pageName="Research"><OutreachScope need="master"><OutreachResearch /></OutreachScope></PageBoundary>              : <Navigate to="/" replace />} />
            <Route path="/outreach/research/:id"  element={canUseTool(me, "outreach") ? <PageBoundary pageName="Research run"><OutreachScope need="master"><OutreachResearchRun /></OutreachScope></PageBoundary>       : <Navigate to="/" replace />} />
            <Route path="/outreach/roles"         element={canUseTool(me, "outreach") ? <PageBoundary pageName="Roles"><OutreachScope need="master"><OutreachRoles /></OutreachScope></PageBoundary>                    : <Navigate to="/" replace />} />
            <Route path="/outreach/senders"       element={canUseTool(me, "outreach") ? <PageBoundary pageName="Senders"><OutreachScope need="master"><OutreachSenders /></OutreachScope></PageBoundary>                : <Navigate to="/" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </QueryClientProvider>
    </MeContext.Provider>
  );
}
